import { useCallback, useMemo, useState } from 'react';
import type { BadgeUniverso, ParticipantBadge } from 'shared';
import { BADGE_UNIVERSO_COLORS, hasCertificate } from 'shared';
import { downloadCertificate } from '@/lib/certificate';
import { printBadgeSummary } from '@/lib/certificateSummary';

const formatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

export interface BadgesListProps {
  isLoading: boolean;
  isError: boolean;
  badges: ParticipantBadge[];
  participantName: string;
  /** False while the participant record (and so the template) is still loading. */
  participantReady: boolean;
  /** Artwork key of the digger's centre, or null when no certificate can be issued. */
  certificateTemplate: string | null;
  centerName: string | null;
  /** True when a guardian is looking at their educando's badges. */
  forGuardian?: boolean;
}

export default function BadgesList({
  isLoading,
  isError,
  badges,
  participantName,
  participantReady,
  certificateTemplate,
  centerName,
  forGuardian = false,
}: BadgesListProps) {
  // Id of the badge whose certificate is currently rendering, so only that row
  // shows a busy state.
  const [pendingId, setPendingId] = useState<number | null>(null);
  const [printing, setPrinting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Badges the digger unticked for the printed certificate. Kept as an
  // exclusion set so a refetched list needs no resync: new badges start ticked.
  const [excluded, setExcluded] = useState<ReadonlySet<number>>(() => new Set());

  const eligible = useMemo(() => badges.filter(hasCertificate), [badges]);
  const canCertify = certificateTemplate != null;
  const selected = useMemo(
    () => eligible.filter((b) => !excluded.has(b.id)),
    [eligible, excluded],
  );

  const toggle = useCallback((id: number) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleDownload = useCallback(
    async (badge: ParticipantBadge) => {
      if (!certificateTemplate) return;
      setActionError(null);
      setPendingId(badge.id);
      try {
        await downloadCertificate(badge, participantName, certificateTemplate);
      } catch {
        setActionError('Não foi possível gerar o certificado. Tente novamente mais tarde.');
      } finally {
        setPendingId(null);
      }
    },
    [participantName, certificateTemplate],
  );

  const handlePrint = useCallback(async () => {
    if (!certificateTemplate || selected.length === 0) return;
    setActionError(null);
    setPrinting(true);
    try {
      await printBadgeSummary(selected, participantName, certificateTemplate);
    } catch {
      setActionError('Não foi possível preparar o certificado para impressão. Tente novamente mais tarde.');
    } finally {
      setPrinting(false);
    }
  }, [selected, participantName, certificateTemplate]);

  const showActions = !isLoading && !isError && eligible.length > 0;

  return (
    <section aria-labelledby="badges-heading">
      <div className="mb-3 flex items-center justify-between px-1">
        <h2
          id="badges-heading"
          className="text-xs font-bold uppercase tracking-widest text-muted-foreground"
        >
          {forGuardian ? 'Badges' : 'Os seus badges'}
        </h2>
        {!isLoading && !isError && (
          <span className="rounded-md bg-input-soft px-2 py-0.5 text-xs font-semibold text-muted-foreground">
            {badges.length}
          </span>
        )}
      </div>

      {showActions && canCertify && (
        <div className="mb-3 space-y-2 px-1">
          <p className="text-xs text-muted-foreground">
            {forGuardian
              ? 'Toque num badge para descarregar o certificado, ou imprima um certificado com os badges selecionados.'
              : 'Toque num badge para descarregar o seu certificado, ou imprima um certificado com os badges selecionados.'}
          </p>
          <button
            type="button"
            onClick={handlePrint}
            disabled={printing || selected.length === 0}
            aria-busy={printing || undefined}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary px-4 py-3 text-sm font-bold text-primary-foreground shadow-sm transition hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.99] disabled:opacity-60"
          >
            {printing ? <Spinner light /> : <PrintGlyph />}
            Imprimir certificado ({selected.length})
          </button>
        </div>
      )}

      {showActions && !canCertify && participantReady && (
        <p className="mb-3 px-1 text-xs text-muted-foreground">
          {centerName
            ? `O ${centerName} ainda não tem modelo de certificado.`
            : forGuardian
              ? 'Os certificados ficam disponíveis depois da primeira visita a um centro.'
              : 'Os certificados ficam disponíveis depois da sua primeira visita a um centro.'}
        </p>
      )}

      {isLoading && <BadgesSkeleton />}

      {isError && !isLoading && (
        <div
          role="alert"
          className="rounded-2xl border border-busy/50 bg-busy-soft p-4 text-sm text-busy"
        >
          Não foi possível carregar os badges. Tente novamente mais tarde.
        </div>
      )}

      {actionError && (
        <div
          role="alert"
          className="mb-3 rounded-2xl border border-busy/50 bg-busy-soft p-4 text-sm text-busy"
        >
          {actionError}
        </div>
      )}

      {!isLoading && !isError && badges.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border-strong bg-card p-6 text-center text-sm text-muted-foreground">
          {forGuardian ? 'Ainda não há badges atribuídos.' : 'Ainda não tem badges atribuídos.'}
        </div>
      )}

      {badges.length > 0 && (
        <ul className="space-y-2">
          {badges.map((badge) => (
            <BadgeRow
              key={badge.id}
              badge={badge}
              downloadable={canCertify && hasCertificate(badge)}
              selected={!excluded.has(badge.id)}
              isPending={pendingId === badge.id}
              onDownload={handleDownload}
              onToggle={toggle}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

interface BadgeRowProps {
  badge: ParticipantBadge;
  downloadable: boolean;
  selected: boolean;
  isPending: boolean;
  onDownload: (badge: ParticipantBadge) => void;
  onToggle: (id: number) => void;
}

/**
 * One badge row. Badges that carry a certificate render as a button that
 * downloads it, with a checkbox that includes them in the printed certificate;
 * the rest — revoked assignments, the `Link` universe, or every badge while
 * the centre has no template — render as a plain, non-interactive row.
 */
function BadgeRow({ badge, downloadable, selected, isPending, onDownload, onToggle }: BadgeRowProps) {
  const revoked = badge.revokedAt != null;

  const inner = (
    <>
      <div
        className={
          revoked
            ? 'flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-input-soft text-muted-foreground'
            : 'flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary'
        }
      >
        <BadgeGlyph />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-bold text-foreground">{badge.badgeName}</p>
          {badge.badgeUniverso && (
            <UniversoChip universo={badge.badgeUniverso} muted={revoked} />
          )}
        </div>
        {badge.description && (
          <p className="truncate text-xs text-muted-foreground">{badge.description}</p>
        )}
        <p className="truncate text-xs text-muted-foreground">
          Atribuído em {formatDate(badge.assignedAt)}
        </p>
        {!!badge.badgeLabels?.length && (
          <div className="mt-1 flex flex-wrap gap-1">
            {badge.badgeLabels.map((label) => (
              <span
                key={label}
                className="rounded-md bg-input-soft px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
              >
                {label}
              </span>
            ))}
          </div>
        )}
      </div>
      {revoked && (
        <span className="rounded-md bg-input-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Revogado
        </span>
      )}
      {isPending && <Spinner />}
    </>
  );

  if (!downloadable) {
    return (
      <li className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3 shadow-sm">
        {inner}
      </li>
    );
  }

  return (
    <li className="flex items-center gap-2">
      <input
        type="checkbox"
        checked={selected}
        onChange={() => onToggle(badge.id)}
        aria-label={`Incluir ${badge.badgeName} no certificado impresso`}
        className="h-5 w-5 flex-shrink-0 rounded accent-primary"
      />
      <button
        type="button"
        onClick={() => onDownload(badge)}
        disabled={isPending}
        aria-label={`Descarregar certificado de ${badge.badgeName}`}
        aria-busy={isPending || undefined}
        className="flex min-w-0 flex-1 items-center gap-3 rounded-2xl border border-border bg-card p-3 text-left shadow-sm transition hover:border-border-strong hover:bg-input-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.99] disabled:opacity-60"
      >
        {inner}
      </button>
    </li>
  );
}

function BadgesSkeleton() {
  return (
    <ul className="space-y-2">
      {[0, 1, 2].map((i) => (
        <li
          key={i}
          className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3"
        >
          <div className="h-11 w-11 flex-shrink-0 animate-pulse rounded-2xl bg-input-soft" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-2/3 animate-pulse rounded bg-input-soft" />
            <div className="h-2 w-1/3 animate-pulse rounded bg-input-soft" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function Spinner({ light = false }: { light?: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={`h-4 w-4 flex-shrink-0 animate-spin ${light ? 'text-primary-foreground' : 'text-muted-foreground'}`}
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth={3} opacity={0.25} />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth={3}
        strokeLinecap="round"
      />
    </svg>
  );
}

function PrintGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-4 w-4 flex-shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path d="M6 9V3h12v6" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="3" y="9" width="18" height="8" rx="2" />
      <path d="M7 14h10v7H7z" strokeLinejoin="round" />
    </svg>
  );
}

function UniversoChip({ universo, muted }: { universo: BadgeUniverso; muted: boolean }) {
  const { bg, text } = BADGE_UNIVERSO_COLORS[universo];
  return (
    <span
      className={`flex-shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider${
        muted ? ' bg-input-soft text-muted-foreground' : ''
      }`}
      style={muted ? undefined : { backgroundColor: bg, color: text }}
    >
      {universo}
    </span>
  );
}

function BadgeGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="8" r="5" strokeLinejoin="round" />
      <path d="M8.5 12.5L7 22l5-3 5 3-1.5-9.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
